import React from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { Breadcrumb, Table, NavLinkButton } from '@shared/components/bootstrap';
import { UserRole } from '@shared/types/user_role';

import { Preview as AddressDetailPreview } from '@admin/components/address/detail/preview';
import { Panel } from '@admin/components/helpers/panel';
import { Roles } from '@admin/components/helpers/roles';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { Scheduled as OrderScheduled } from '@admin/components/orders/scheduled';
import { State as OrderState } from '@admin/components/orders/state';
import { Summary as OrderSummary } from '@admin/components/orders/summary';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { History__Subject, useAccountAddressShowQuery } from '@admin/schema';

export const Show: React.FC = () => {
  useTitle('Address');
  const { accountID, addressID } = useParams<{ accountID: string; addressID: string }>();
  const { data } = useAccountAddressShowQuery({
    client,
    fetchPolicy: 'cache-and-network',
    variables: { accountID, addressID },
  });
  const account = data?.account;
  const address = account?.address;
  const orders = address?.orders;

  return (
    <>
      <h1 className="page-header text-overflow">
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to="/accounts">Accounts</NavLink>
          </Breadcrumb.Item>{' '}
          <Breadcrumb.Item>
            <NavLink to={`/accounts/${accountID}`}>
              #{account?.number ?? '-'} ({account?.customer.name ?? '-'})
            </NavLink>
          </Breadcrumb.Item>{' '}
          <Breadcrumb.Item active>Address</Breadcrumb.Item>
        </Breadcrumb>
      </h1>

      <Panel>
        <Panel.Header>
          <Panel.Title>Details</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <p>
            <strong>Street:</strong> {address?.street ?? '-'}
          </p>
          <p>
            <strong>Apt/Suite:</strong> {address?.aptsuite ?? '-'}
          </p>
          <p>
            <strong>Business Name:</strong> {address?.business_name ?? '-'}
          </p>
          <p>
            <strong>City:</strong> {address?.city ?? '-'}
          </p>
          <p>
            <strong>Neighborhood:</strong> {address?.neighborhood ?? '-'}
          </p>
          <p>
            <strong>State:</strong> {address?.state ?? '-'}
          </p>
          <p>
            <strong>Country:</strong> {address?.country ?? '-'}
          </p>
          <p>
            <strong>ZIP:</strong> {address?.zip ?? '-'}
          </p>
          <p>
            <strong>Latitude:</strong> {address?.latitude ?? '-'}
          </p>
          <p>
            <strong>Longitude:</strong> {address?.longitude ?? '-'}
          </p>
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Header>
          <Panel.Title>Settings</Panel.Title>
        </Panel.Header>
        <Panel.Body>{address?.details && <AddressDetailPreview address={address.details} />}</Panel.Body>
        <Panel.Footer align="right">
          <NavLinkButton to={`/accounts/${accountID}`} kind="default">
            Back
          </NavLinkButton>
          <Roles hide={[UserRole.L1Agent]}>
            <NavLinkButton to={`/accounts/${accountID}/addresses/${addressID}/edit`} kind="primary">
              Edit
            </NavLinkButton>
          </Roles>
        </Panel.Footer>
      </Panel>

      <Panel>
        <Panel.Header>
          <Panel.Title>Orders</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <Table bordered striped>
            <thead>
              <tr>
                <th>Number</th>
                <th>Kind</th>
                <th>Scheduled</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orders?.map((order) => (
                <tr key={order.id}>
                  <td>#{order.number}</td>
                  <td>
                    <OrderSummary order={order} />
                  </td>
                  <td>
                    <OrderScheduled order={order} />
                  </td>
                  <td>
                    <OrderState state={order.state} waitlisted={order.waitlistRequests.length > 0} />
                  </td>
                  <td>
                    <NavLinkButton kind="primary" to={`/accounts/${accountID}/orders/${order.id}`} target="_blank">
                      Details
                    </NavLinkButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Panel.Body>
      </Panel>

      <HistoriesPanel type={History__Subject.Address} id={addressID} />
    </>
  );
};
